import React, { useState } from "react";
import DeleteButton from "@/ui-controls/DeleteButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import { Grid } from "fluentui-react-grid";
import { IconButton, Stack, Text } from "@fluentui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Application,
  deleteApplication as deleteApplicationApi,
} from "@/api/applications";

interface Props {
  application: Application;
  onDelete?: (app: Application) => any;
  onClose?: () => any;
}

const DeleteApplicationForm = (props: Props) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const onCancelForm = () => {
    if (props.onClose) props.onClose();
  };

  const handleCopyName = async () => {
    if (!props.application?.Name) {
      return;
    }
    try {
      await navigator.clipboard.writeText(props.application.Name);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = props.application.Name;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const deleteApplication = () => {
    setIsDeleting(true);

    deleteApplicationApi(props.application.Id).then(
      (result: boolean) => {
        setIsDeleting(false);
        if (result) {
          if (props.onDelete) props.onDelete(props.application);
        }
      },
      (error) => {
        setIsDeleting(false);
        handleError(error);
      },
    );
  };

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .max(250)
        .oneOf([props.application?.Name], "Invalid application name"),
    }),
    onSubmit: (values) => {
      deleteApplication();
    },
  });

  const handleError = (error: any) => {};

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Grid dir="ltr">
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              {props.application && (
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  styles={{ root: { marginBottom: "8px" } }}
                >
                  <Text
                    variant="smallPlus"
                    styles={{ root: { fontWeight: 600 } }}
                  >
                    {props.application.Name}
                  </Text>
                  <IconButton
                    iconProps={{ iconName: "Copy" }}
                    title="Copy application name"
                    ariaLabel="Copy application name"
                    onClick={handleCopyName}
                  />
                </Stack>
              )}
              <TextInput
                label="Confirm Application Name"
                baseTypeProps={{
                  disabled: isDeleting,
                  ...formik.getFieldProps("name"),
                }}
                hasError={formik.errors.name && formik.touched.name}
                errorMessage={formik.errors.name}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <DeleteButton
                text="Delete"
                baseTypeProps={{ type: "submit", disabled: isDeleting }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={onCancelForm}
                baseTypeProps={{ disabled: isDeleting }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </form>
    </>
  );
};

export default DeleteApplicationForm;
